<script lang="ts" setup>
import { PaginationResource } from '~/resources/PaginationResource'

const props = defineProps<{
  pagination?: PaginationResource | null
}>()
const emit = defineEmits<{
  (e: 'goTo', page: number): void
}>()

const currentPage = computed(() => props.pagination?.current_page ?? 1)
const lastPage = computed(() => props.pagination?.last_page ?? 1)
const range = 5

const firstPageRange = [...Array(range)].map((_, index) => index + 1)
const lastPageRange = computed(() => [...Array(range)].map((_, index) => lastPage.value - index))
const currentPageRange = computed(() => [currentPage.value - 2, currentPage.value - 1, currentPage.value, currentPage.value + 1, currentPage.value + 2])

const shouldShowPage = (page: number) => {
  // We are on one of the first 5 pages. we always show these, also we show the last one
  if (firstPageRange.includes(currentPage.value)) {
    return [...firstPageRange, lastPage.value].includes(page)
    // If we are on one of the 5 last pages we show the last 5 pages, also the first one
  } else if (lastPageRange.value.includes(currentPage.value)) {
    return [...lastPageRange.value, 1].includes(page)
    // Else we only show the current page + the 2 closest + the first and last
  } else {
    return [1, lastPage.value, ...currentPageRange.value].includes(page)
  }
}

const shouldShowEllipsis = (page: number) => {
  // If we show this page already dont show ellipsis here
  if (shouldShowPage(page)) return false
  // If in the first range check we show ellipsis just after the range on button ${range + 1}
  if (firstPageRange.includes(currentPage.value)) return page === range + 1
  // If in the last range check we show ellipsis just before the lastPage - range on button ${lastPage - range +}
  if (lastPageRange.value.includes(currentPage.value)) return page === lastPage.value - range
  // otherwise we show the button within the closes 2, eq: page 10 shows ellipsis just before 8 and after 12
  return (page === currentPage.value + 3 || page === currentPage.value - 3)
}

const goToPage = (page: number) => {
  emit('goTo', page)
}
</script>

<template>
  <nav aria-label="pagination" class="l-cluster pagination">
    <ul>
      <li>
        <button class="button active" :disabled="currentPage === 1 ? true : undefined" @click="goToPage(currentPage - 1)">
          <i class="fas fa-arrow-left"></i>
          {{$t('pagination.previous')}} <span class="sr-only">{{$t('pagination.page')}}</span>
        </button>
      </li>
      <template v-for="page in lastPage">
        <li :key="page" v-if="shouldShowPage(page)">
          <button class="button" 
                  :class="{active: page === currentPage}" 
                  :aria-current="currentPage === page ? 'page' : undefined"
                  @click="goToPage(page)"
              >
            <span class="sr-only" v-if="currentPage !== page">page</span>
            {{ page }}
          </button>
        </li>
        <li v-if="shouldShowEllipsis(page)" aria-hidden="true">
          <button class="button ellipsis" disabled :title="`${page}`">&#8230;</button>
        </li>
      </template>
      <li>
        <button class="button active" :disabled="currentPage === lastPage" @click="goToPage(currentPage + 1)">
          {{$t('pagination.next')}} <span class="sr-only">{{$t('pagination.page')}}</span>
          <i class="fas fa-arrow-right"></i>
        </button>
      </li>
    </ul>
  </nav>
</template>

<style lang="postcss" scoped>
nav {
  & ul {
    display: flex;
    align-items: center;
    list-style: none;
    flex-wrap: nowrap;
    overflow: auto;
    padding: 0;
    gap: 0.8rem;
    & li {
      display: flex;
      &:first-child,
      &:last-child {
        position: sticky;
        flex-basis: 0%;
        flex-grow: 1;
        flex-shrink: 1;
        z-index: 1;
      }
      &:first-child {
        left: 0;
      }
      &:last-child {
        right: 0;
        justify-content: end;
      }
      & button {
        padding: 0.8rem 1.4rem;
        border: 1px solid transparent;
        color: var(--secondary-color);
        border-radius: var(--border-radius-small);
        background-color: var(--background-color-primary);
        will-change: filter, transform, outline, border;
        /* transition: 0s; */
        &[aria-current],
        &.ellipsis,
        &:disabled {
          cursor: default;
          pointer-events: none;
        }
        &:not(:is(.ellipsis)) {
          &.active,
          &:hover,
          &:focus-visible {
            border-color: #d0d5dd;
            filter: none;
          }
        }
      }
    }
  }
}
</style>
